import { Box, Flex, Image, Text, Tooltip, VStack, useClipboard, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { AddressParam } from 'types/api/addressParams';
import type { Transaction } from 'types/api/transaction';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import dayjs from 'lib/date/dayjs';
import getCurrencyValue from 'lib/getCurrencyValue';
import useIsSticky from 'lib/hooks/useIsSticky';
import useLazyRenderedList from 'lib/hooks/useLazyRenderedList';
import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';
import LinkInternal from 'ui/shared/links/LinkInternal';
import ScanningNotice, { AnimatedDots } from 'ui/shared/ScanningNotice';
import FinanceStatusTag from 'ui/shared/statusTag/FinanceStatusTag';
import TxStatus from 'ui/shared/statusTag/TxStatus';

import { TXS_HEADER_KEYS } from './i18n';
import TxAdditionalInfo from './TxAdditionalInfo';

const ACCENT_COLOR = '#14DCAC';

type Props = {
  txs: Array<Transaction>;
  showBlockInfo: boolean;
  showSocketInfo: boolean;
  socketInfoAlert?: string;
  socketInfoNum?: number;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  top: number;
  desktopTopSlot?: React.ReactNode;
  hideValueUsd?: boolean;
  noBorder?: boolean;
};

type ColumnKey = 'hash' | 'dateTime' | 'block' | 'fromTo' | 'amount' | 'method' | 'valueUsd' | 'fee';

const getColumns = (hideValueUsd?: boolean, showBlockInfo?: boolean): Array<{ key: ColumnKey; width: string }> => {
  const columns: Array<{ key: ColumnKey; width: string }> = [
    { key: 'hash', width: '136px' },
    { key: 'dateTime', width: '96px' },
    { key: 'block', width: '96px' },
    { key: 'fromTo', width: '208px' },
    { key: 'amount', width: '110px' },
    { key: 'method', width: '110px' },
    { key: 'valueUsd', width: '110px' },
    { key: 'fee', width: '110px' },
  ];

  return columns.filter((column) => {
    if (column.key === 'valueUsd' && hideValueUsd) {
      return false;
    }
    if (column.key === 'block' && !showBlockInfo) {
      return false;
    }
    return true;
  });
};

const useExplorerTheme = () => {
  const isLight = useColorModeValue(true, false);

  return {
    isLight,
    borderColor: useColorModeValue('#E9ECEF', '#222222'),
    surfaceColor: useColorModeValue('#FFFFFF', '#111111'),
    subSurfaceColor: useColorModeValue('#FBFCFE', '#171717'),
    primaryText: useColorModeValue('#000000', '#FFFFFF'),
    secondaryText: useColorModeValue('#616161', '#999999'),
    valueText: useColorModeValue('#616161', '#999999'),
    noticeBg: useColorModeValue(
      'linear-gradient(90deg, rgba(20, 220, 172, 1) 0%, rgba(255, 255, 255, 1) 100%)',
      'linear-gradient(90deg, rgba(20, 220, 172, 0.10) 0%, rgba(20, 20, 20, 0.10) 100%)',
    ),
    noticeText: '#FFFFFF',
    noticeAccentColor: useColorModeValue('#FFFFFF', ACCENT_COLOR),
    stickyShadow: useColorModeValue('0 12px 24px rgba(31, 31, 34, 0.08)', '0 12px 24px rgba(0, 0, 0, 0.35)'),
    rowHoverBg: useColorModeValue('#FBFCFE', 'rgba(255,255,255,0.01)'),
    eyeButtonSrc: isLight ? '/static/transactions/tx-eye-light.svg' : '/static/transactions/tx-eye.svg',
    eyeButtonSize: isLight ? '28px' : '16px',
    headerInfoSrc: isLight ? '/static/transactions/tx-header-info-light.svg' : '/static/transactions/tx-header-info.svg',
  };
};

const getRowTextSx = (color: string) => ({
  color,
  fontFamily: '\'Oxanium\', sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
});

const getSecondaryTextSx = (color: string) => ({
  color,
  fontFamily: '\'Space Grotesk\', sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
});

const getValueTextSx = (color: string) => ({
  color,
  fontFamily: '\'Oxanium\', sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '24px',
});

const getHeaderLabelSx = (color: string) => ({
  color,
  fontFamily: '\'Space Grotesk\', sans-serif',
  fontWeight: 700,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
});

const getAddressLabel = (address: AddressParam | null) => {
  if (!address) {
    return '-';
  }

  return address.name || address.ens_domain_name || `${ address.hash.slice(0, 8) }...${ address.hash.slice(-8) }`;
};

const CopyButton = ({ text }: { text: string }) => {
  const { primaryText } = useExplorerTheme();
  const { t } = useI18n();
  const { hasCopied, onCopy } = useClipboard(text, 1000);
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onCopy();
  }, [ onCopy ]);

  return (
    <Tooltip label={ hasCopied ? t('common.copied') : t('common.copyToClipboard') } color="white">
      <Box
        as="button"
        type="button"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize="16px"
        flexShrink={ 0 }
        onClick={ handleClick }
      >
        <Image src="/static/transactions/tx-copy.svg" alt="" boxSize="16px" color={ primaryText }/>
      </Box>
    </Tooltip>
  );
};

const ExplorerAddressLine = ({
  label,
  address,
  isLoading,
}: {
  label: string;
  address: AddressParam | null;
  isLoading?: boolean;
}) => {
  const { secondaryText, primaryText } = useExplorerTheme();
  const secondaryTextSx = getSecondaryTextSx(secondaryText);
  const rowTextSx = getRowTextSx(primaryText);

  if (isLoading) {
    return (
      <Flex alignItems="center" gap="4px" minW={ 0 }>
        <Text { ...secondaryTextSx } flexShrink={ 0 }>{ label }</Text>
        <Skeleton isLoaded={ false } h="20px" w="132px" borderRadius="4px"/>
        <Skeleton isLoaded={ false } boxSize="16px" borderRadius="4px" flexShrink={ 0 }/>
      </Flex>
    );
  }

  if (!address) {
    return null;
  }

  return (
    <Flex alignItems="center" gap="4px" minW={ 0 }>
      <Text { ...secondaryTextSx } flexShrink={ 0 }>{ label }</Text>
      <Flex alignItems="center" gap="4px" minW={ 0 } flex={ 1 }>
        <LinkInternal
          href={ route({ pathname: '/address/[hash]', query: { hash: address.hash } }) }
          display="inline-flex"
          alignItems="center"
          minW={ 0 }
          flex={ 1 }
          _hover={{ textDecoration: 'none' }}
        >
          <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            { getAddressLabel(address) }
          </Text>
        </LinkInternal>
        <CopyButton text={ address.hash }/>
      </Flex>
    </Flex>
  );
};

const ExplorerTxHash = ({ tx, isLoading }: { tx: Transaction; isLoading?: boolean }) => {
  const { primaryText } = useExplorerTheme();
  const rowTextSx = getRowTextSx(primaryText);

  if (isLoading) {
    return (
      <Box width="136px">
        <Flex alignItems="center" gap="4px" minW={ 0 }>
          <Skeleton isLoaded={ false } h="20px" w="108px" borderRadius="4px"/>
          <Skeleton isLoaded={ false } boxSize="16px" borderRadius="4px" flexShrink={ 0 }/>
        </Flex>
        <Box mt="4px">
          <Skeleton isLoaded={ false } h="24px" w="100px" borderRadius="4px"/>
        </Box>
      </Box>
    );
  }

  return (
    <Box width="136px">
      <Flex alignItems="center" gap="4px" minW={ 0 }>
        <LinkInternal
          href={ route({ pathname: '/tx/[hash]', query: { hash: tx.hash } }) }
          display="inline-flex"
          alignItems="center"
          minW={ 0 }
          flex={ 1 }
          _hover={{ textDecoration: 'none' }}
        >
          <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            { tx.hash }
          </Text>
        </LinkInternal>
        <CopyButton text={ tx.hash }/>
      </Flex>
      <Box mt="4px">
        { tx.status_label ? (
          <FinanceStatusTag status={ tx.status_label } isLoading={ isLoading }/>
        ) : (
          <TxStatus
            status={ tx.status }
            errorText={ tx.status === 'error' ? tx.result : undefined }
            isLoading={ isLoading }
            variant="explorer"
          />
        ) }
      </Box>
    </Box>
  );
};

const ExplorerCellTag = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <Flex width="96px" minW="96px" minH="52px" alignItems="center">
    { children }
  </Flex>
);

const ExplorerPill = ({ children }: { children: React.ReactNode }) => {
  const { borderColor, subSurfaceColor, primaryText } = useExplorerTheme();

  return (
    <Flex
      width="96px"
      h="24px"
      px="8px"
      justifyContent="center"
      alignItems="center"
      border="1px solid"
      borderColor={ borderColor }
      borderRadius="4px"
      bg={ subSurfaceColor }
    >
      <Text
        color={ primaryText }
        fontFamily="'Space Grotesk', sans-serif"
        fontWeight={ 500 }
        fontStyle="normal"
        fontSize="12px"
        lineHeight="16px"
        textAlign="center"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        width="100%"
      >
        { children }
      </Text>
    </Flex>
  );
};

const ExplorerValue = ({ value, exchangeRate, isLoading }: { value: string; exchangeRate?: string | null; isLoading?: boolean }) => {
  const { valueText } = useExplorerTheme();
  const valueTextSx = getValueTextSx(valueText);

  if (isLoading) {
    return <Skeleton isLoaded={ false } h="24px" w="72px" borderRadius="4px" ml="auto"/>;
  }

  const formatted = getCurrencyValue({
    value,
    exchangeRate,
    decimals: String(config.chain.currency.decimals),
    accuracy: 8,
  });

  return (
    <Text { ...valueTextSx } textAlign="right" width="96px">
      { formatted.valueStr }
    </Text>
  );
};

const ExplorerTxRow = ({
  tx,
  showBlockInfo,
  isLoading,
  hideValueUsd,
}: {
  tx: Transaction;
  showBlockInfo?: boolean;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  hideValueUsd?: boolean;
}) => {
  const { t } = useI18n();
  const {
    isLight,
    borderColor,
    subSurfaceColor,
    primaryText,
    secondaryText,
    valueText,
    rowHoverBg,
    eyeButtonSrc,
    eyeButtonSize,
  } = useExplorerTheme();
  const secondaryTextSx = getSecondaryTextSx(secondaryText);

  const methodContent = (() => {
    if (isLoading) {
      return <Skeleton isLoaded={ false } h="24px" w="96px" borderRadius="4px"/>;
    }

    if (!tx.method) {
      return null;
    }

    return <ExplorerPill>{ tx.method }</ExplorerPill>;
  })();
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gap="16px"
      px="16px"
      py="8px"
      minH="68px"
      borderBottom="1px solid"
      borderColor={ borderColor }
      _hover={{ bg: rowHoverBg }}
    >
      <Flex width="28px" minW="28px" h="52px" alignItems="center" justifyContent="center">
        { isLight ? (
          <TxAdditionalInfo
            tx={ tx }
            isLoading={ isLoading }
            buttonIconSrc={ eyeButtonSrc }
            buttonIconSize={ eyeButtonSize }
          />
        ) : (
          <Box
            width="28px"
            height="28px"
            border="1px solid"
            borderColor={ borderColor }
            borderRadius="4px"
            bg={ subSurfaceColor }
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <TxAdditionalInfo
              tx={ tx }
              isLoading={ isLoading }
              buttonIconSrc={ eyeButtonSrc }
              buttonIconSize={ eyeButtonSize }
            />
          </Box>
        ) }
      </Flex>

      <ExplorerTxHash tx={ tx } isLoading={ isLoading }/>

      { /* Date Time */ }
      <Flex width="96px" minW="96px" minH="52px" alignItems="center">
        { isLoading ? (
          <VStack spacing="4px" alignItems="start">
            <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
            <Skeleton isLoaded={ false } h="20px" w="56px" borderRadius="4px"/>
          </VStack>
        ) : (
          <VStack spacing="0" alignItems="start">
            <Text { ...secondaryTextSx } lineHeight="24px">
              { tx.timestamp ? dayjs(tx.timestamp).format('YYYY/MM/DD') : '-' }
            </Text>
            <Text { ...secondaryTextSx } lineHeight="24px">
              { tx.timestamp ? dayjs(tx.timestamp).format('HH:mm:ss') : '-' }
            </Text>
          </VStack>
        ) }
      </Flex>

      { /* Block */ }
      { showBlockInfo && (
        <Flex width="96px" minW="96px" minH="52px" alignItems="center">
          { (() => {
            if (isLoading) {
              return <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>;
            }

            if (!tx.block_number) {
              return <Text { ...getValueTextSx(valueText) }>-</Text>;
            }

            return (
              <LinkInternal
                href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(tx.block_number) } }) }
                display="inline-flex"
                alignItems="center"
                minW={ 0 }
                _hover={{ textDecoration: 'none' }}
              >
                <Text { ...getRowTextSx(primaryText) } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  { tx.block_number }
                </Text>
              </LinkInternal>
            );
          })() }
        </Flex>
      ) }

      { /* From/To */ }
      <Box width="208px" minW="208px">
        <ExplorerAddressLine label={ t('txs.from') } address={ tx.from } isLoading={ isLoading }/>
        <Box mt="4px">
          <ExplorerAddressLine label={ t('txs.to') } address={ tx.to || tx.created_contract } isLoading={ isLoading }/>
        </Box>
      </Box>

      { /* Amount */ }
      <Flex width="110px" minW="110px" minH="52px" alignItems="center">
        { isLoading ? (
          <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
        ) : (
          <Text { ...getValueTextSx(valueText) }>
            { getCurrencyValue({ value: tx.value, decimals: '18', accuracy: 8 }).valueStr || '-' }
          </Text>
        ) }
      </Flex>

      { /* Method */ }
      <ExplorerCellTag>
        { methodContent }
      </ExplorerCellTag>

      { !hideValueUsd && (
        <Flex width="110px" minW="110px" minH="52px" alignItems="center" justifyContent="flex-end">
          { isLoading ? (
            <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
          ) : (
            <Text { ...getValueTextSx(valueText) } textAlign="right">
              { tx.value_usd || 'N/A' }
            </Text>
          ) }
        </Flex>
      ) }

      { /* Txn Fee (BOT) */ }
      <Flex width="110px" minW="110px" minH="52px" alignItems="center" justifyContent="flex-end">
        <ExplorerValue value={ tx.fee.value || '0' } isLoading={ isLoading }/>
      </Flex>
    </Flex>
  );
};

const ExplorerTxsDesktop = ({
  txs,
  showBlockInfo,
  showSocketInfo,
  socketInfoAlert,
  socketInfoNum,
  enableTimeIncrement,
  isLoading,
  top,
  desktopTopSlot,
  hideValueUsd,
  noBorder,
}: Props) => {
  const { t } = useI18n();
  const {
    isLight,
    borderColor,
    surfaceColor,
    primaryText,
    secondaryText,
    noticeAccentColor,
    stickyShadow,
    headerInfoSrc,
  } = useExplorerTheme();
  const { cutRef, renderedItemsNum } = useLazyRenderedList(txs, !isLoading);
  const stickySentinelRef = React.useRef<HTMLDivElement>(null);
  const isSticky = useIsSticky(stickySentinelRef, top, true);
  const headerScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);

  const handleBodyScroll = React.useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);
  const columns = getColumns(hideValueUsd, showBlockInfo);
  const headerLabelSx = getHeaderLabelSx(primaryText);
  const noticeContent = (() => {
    if (socketInfoAlert) {
      return (
        <Text fontFamily="'Space Grotesk', sans-serif" fontSize="14px" lineHeight="20px" fontWeight={ 400 }>
          { socketInfoAlert }
        </Text>
      );
    }

    if (socketInfoNum) {
      return (
        <Text fontFamily="'Space Grotesk', sans-serif" fontSize="14px" lineHeight="20px" fontWeight={ 400 }>
          <Text as="span" color={ noticeAccentColor }>
            { t('common.socketNotice.transactionsOther', { count: socketInfoNum.toLocaleString() }) }
          </Text> { t('common.socketNotice.haveComeIn') }
        </Text>
      );
    }

    // strip the trailing dots from the localized string; they are rendered as an animation instead
    const scanningText = t('common.socketNotice.scanningTransactions').replace(/[.…\s]+$/, '');
    return (
      <Text fontFamily="'Space Grotesk', sans-serif" fontSize="14px" lineHeight="20px" fontWeight={ 400 }>
        { scanningText }<AnimatedDots/>
      </Text>
    );
  })();

  return (
    <Box
      bg={ surfaceColor }
      { ...(noBorder ? {} : { border: '1px solid', borderColor, borderRadius: '8px' }) }
      overflow="visible"
    >
      <Box ref={ stickySentinelRef } h={ 0 }/>
      <Box
        position="sticky"
        top={ `${ top }px` }
        zIndex={ 4 }
        transition="box-shadow 0.2s ease, transform 0.2s ease"
      >
        <Box
          bg={ surfaceColor }
          pt="16px"
          pb="8px"
          boxShadow={ isSticky ? stickyShadow : 'none' }
        >
          { desktopTopSlot && (
            <Box px="16px" pb="16px">
              { desktopTopSlot }
            </Box>
          ) }

          { showSocketInfo && (isLoading || socketInfoAlert || socketInfoNum) ? (
            <Box mb="4px">
              <ScanningNotice>{ noticeContent }</ScanningNotice>
            </Box>
          ) : null }

          { /* Column headers — scroll is synced with body via JS */ }
          <Box ref={ headerScrollRef } overflowX="hidden">
            <Flex
              px="16px"
              justifyContent="space-between"
              alignItems="center"
              gap="16px"
              pb="8px"
              borderBottom="1px solid"
              borderColor={ borderColor }
            >
              <Flex width="28px" minW="28px" h="32px" alignItems="center" justifyContent="center">
                { isLight ? (
                  <Image src={ headerInfoSrc } alt="" width="28px" height="32px"/>
                ) : (
                  <Image src={ headerInfoSrc } alt="" boxSize="16px"/>
                ) }
              </Flex>
              { columns.map((column) => {
                const titleMap: Record<ColumnKey, string> = {
                  hash: t(TXS_HEADER_KEYS.hash),
                  dateTime: t(TXS_HEADER_KEYS.dateTime),
                  block: t('common.block'),
                  fromTo: t(TXS_HEADER_KEYS.fromTo),
                  amount: t(TXS_HEADER_KEYS.amount),
                  method: t(TXS_HEADER_KEYS.method),
                  valueUsd: t(TXS_HEADER_KEYS.valueUsd),
                  fee: t('txs.txnFeeSymbol', { symbol: config.chain.currency.symbol }),
                };

                return (
                  <Flex
                    key={ column.key }
                    width={ column.width }
                    minW={ column.width }
                    h="32px"
                    alignItems="center"
                    justifyContent={ column.key === 'valueUsd' || column.key === 'fee' ? 'flex-end' : 'flex-start' }
                  >
                    <Text { ...headerLabelSx }>{ titleMap[column.key] }</Text>
                  </Flex>
                );
              }) }
            </Flex>
          </Box>
        </Box>
      </Box>

      { /* Body rows — horizontally scrollable on mobile, syncs header via JS */ }
      <Box
        ref={ bodyScrollRef }
        overflowX={{ base: 'auto', lg: 'visible' }}
        onScroll={ handleBodyScroll }
      >
        { txs.length > 0 ? (
          <Box>
            { txs.slice(0, renderedItemsNum).map((tx, index) => (
              <ExplorerTxRow
                key={ tx.hash + (isLoading ? index : '') }
                tx={ tx }
                showBlockInfo={ showBlockInfo }
                enableTimeIncrement={ enableTimeIncrement }
                isLoading={ isLoading }
                hideValueUsd={ hideValueUsd }
              />
            )) }
          </Box>
        ) : (
          <Flex direction="column" alignItems="center" justifyContent="center" py="80px" gap="16px">
            <Image
              src={ isLight ? '/static/transactions/tx-no-data-light.svg' : '/static/transactions/tx-no-data.svg' }
              alt=""
              boxSize="64px"
            />
            <Text
              color={ secondaryText }
              fontFamily="'Space Grotesk', sans-serif"
              fontSize="14px"
              lineHeight="20px"
              fontWeight={ 400 }
            >
              { t('txs.noDataYet') }
            </Text>
          </Flex>
        ) }
        <Box ref={ cutRef } h={ 0 }/>
      </Box>
    </Box>
  );
};

export default React.memo(ExplorerTxsDesktop);
